/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Helper: session Manager.
 */

/**
 * Device session tracking for single-login enforcement.
 *
 * The API owns the session registry; this module holds the id for the current browser and makes
 * sure every tab of that browser shares it. Storing in localStorage (not sessionStorage) is
 * deliberate: separate tabs are the same device and must not evict each other.
 */

const SESSION_KEY = 'fl.sessionId';
const EVICT_SIGNAL_KEY = 'fl.sessionEvictedAt';
const SIGNED_OUT_REASON_KEY = 'fl.signedOutReason';

const HEARTBEAT_INTERVAL_MS = 60_000;

let inFlightClaim = null;
let evictionHandler = null;
let evicted = false;
let heartbeatTimer = null;

export function getSessionId() {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch (e) {
    return null;
  }
}

function storeSessionId(sessionId) {
  try {
    window.localStorage.setItem(SESSION_KEY, sessionId);
  } catch (e) {
    console.warn('Could not persist session id:', e);
  }
}

export function clearSessionId() {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch (e) {
    /* nothing useful to do */
  }
}

/**
 * Resolve the session id for this browser, claiming one from the API if needed.
 * Concurrent callers share a single in-flight claim.
 *
 * Refuses to claim once evicted. The sign-out is shown for 20 seconds before it happens, and a
 * stray request during that window would otherwise claim a fresh session and evict the device that
 * just took over, leaving the two bouncing each other indefinitely.
 */
export function ensureSession(claimFn) {
  if (evicted) {
    return Promise.resolve(null);
  }

  const existing = getSessionId();
  if (existing) {
    return Promise.resolve(existing);
  }
  if (!inFlightClaim) {
    inFlightClaim = Promise.resolve()
      .then(claimFn)
      .then((sessionId) => {
        inFlightClaim = null;
        if (sessionId) {
          storeSessionId(sessionId);
          evicted = false;
        }
        return sessionId;
      })
      .catch((error) => {
        inFlightClaim = null;
        throw error;
      });
  }
  return inFlightClaim;
}

export function setEvictionHandler(handler) {
  evictionHandler = handler;
}

/**
 * The session was taken over (or expired). Clear local state, tell the other tabs, and hand off
 * to the registered handler, which must perform a real Auth0 logout.
 */
export function handleEviction(reason) {
  if (evicted) {
    return;
  }
  evicted = true;
  stopHeartbeat();
  clearSessionId();
  try {
    window.localStorage.setItem(EVICT_SIGNAL_KEY, String(Date.now()));
  } catch (e) {
    /* other tabs will find out on their own next request */
  }
  if (evictionHandler) {
    evictionHandler(reason);
  }
}

export function startHeartbeat(heartbeatFn) {
  stopHeartbeat();
  heartbeatTimer = window.setInterval(() => {
    if (evicted || !getSessionId()) {
      return;
    }
    Promise.resolve()
      .then(heartbeatFn)
      .catch(() => {
        // A superseded heartbeat surfaces as a 409 and is handled by the axios interceptor.
      });
  }, HEARTBEAT_INTERVAL_MS);
}

export function stopHeartbeat() {
  if (heartbeatTimer) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/**
 * Survives the Auth0 logout redirect so the landing page can explain why the user was signed out.
 */
export function recordSignedOutReason(reason) {
  try {
    window.localStorage.setItem(SIGNED_OUT_REASON_KEY, reason);
  } catch (e) {
    /* the sign-out still happens, just without an explanation */
  }
}

/** Read and clear the reason, so the message shows exactly once. */
export function consumeSignedOutReason() {
  try {
    const reason = window.localStorage.getItem(SIGNED_OUT_REASON_KEY);
    if (reason) {
      window.localStorage.removeItem(SIGNED_OUT_REASON_KEY);
    }
    return reason;
  } catch (e) {
    return null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === EVICT_SIGNAL_KEY && event.newValue && !evicted) {
      evicted = true;
      stopHeartbeat();
      if (evictionHandler) {
        evictionHandler('SESSION_SUPERSEDED');
      }
    }
  });
}
